import { LegalPage } from "../../components/LegalPage";

const EFFECTIVE = "2026-05-30";

export function Security() {
  return (
    <LegalPage
      title="Security"
      eyebrow="Trust center"
      description="WeConsult Rx security overview: encryption, access control, MFA, audit logging, vulnerability management, and incident response."
      path="/security"
      effective={EFFECTIVE}
    >
      <p>
        WeConsult Rx is built for healthcare. Security and privacy are not
        bolted on — they are enforced by the code, the database, and the test
        suite that gates every release.
      </p>

      <h2>Encryption</h2>
      <ul>
        <li>
          <strong>At rest:</strong> AES-256 on database and file storage.
          Per-tenant storage prefixes prevent cross-tenant object access.
        </li>
        <li>
          <strong>In transit:</strong> TLS 1.2+ on every endpoint. HSTS enforced
          on production domains.
        </li>
        <li>
          <strong>Tenant secrets:</strong> Integration credentials stored in an
          encrypted vault. Secrets are never returned to the client.
        </li>
      </ul>

      <h2>Access control</h2>
      <ul>
        <li>
          Multi-factor authentication enforced for staff accounts on the
          platform.
        </li>
        <li>
          Role-based access (owner · admin · reviewer · staff) with tenant-scoped
          data isolation on every PHI table.
        </li>
        <li>
          Cross-tenant access blocked at the database layer — verified by
          automated isolation testing on every release.
        </li>
        <li>
          Administrative credentials are never present in browser bundles.
        </li>
      </ul>

      <h2>Audit logging</h2>
      <p>
        Every action that touches PHI is logged to an append-only audit trail.
        Logs include actor, action, resource, and sanitized metadata. PHI is
        excluded from metadata by automated safety checks.
      </p>
      <ul>
        <li>Retention: 7 years (HIPAA §164.530(j))</li>
        <li>Export: tenant admins can export their tenant&apos;s audit slice via signed link.</li>
        <li>Rate limits on bulk exports to prevent exfiltration.</li>
      </ul>

      <h2>Application security</h2>
      <ul>
        <li>
          Static type checking on every package in the monorepo.
        </li>
        <li>
          Dependency scanning on every pull request.
        </li>
        <li>
          Honeypot and rate limiting on every public form.
        </li>
        <li>
          Webhook signatures verified for e-signature and notification providers.
        </li>
      </ul>

      <h2>Data residency</h2>
      <p>
        Primary database and storage in US-based, HIPAA-eligible infrastructure.
        AI inference runs in HIPAA-eligible regions only, on de-identified prompts.
      </p>

      <h2>Vulnerability management</h2>
      <p>
        Security researchers — please see our{" "}
        <a href="/responsible-disclosure">responsible disclosure policy</a> and
        email security@weconsultrx.com.
      </p>

      <h2>Incident response</h2>
      <p>
        Security incidents are triaged within one business hour during US business
        hours. Customers are notified per BAA terms (no later than 60 days for
        confirmed breaches; sooner for material incidents). Post-incident reports
        are shared with affected customers.
      </p>

      <h2>Penetration testing</h2>
      <p>
        Automated security testing runs on every release. Third-party penetration
        test scheduled annually beginning Q1 2027. Customers under Professional
        and Enterprise tiers may request a summary letter.
      </p>
    </LegalPage>
  );
}
